import React, { useId, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const InputField = ({
  label,
  placeholder,
  addOn,
  validate,
  id,
  icon,
  iconLeft = true,
  className,
  type="text",
  value,
  onChange,
  disabled = false,
  maxLength,
}) => {
  const [isValid, setIsValid] = useState(null);

  // Consumers rarely pass an id, and without one the label's htmlFor points at nothing:
  // the field has no accessible name and clicking the label does not focus it.
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const isNumber = type === "number";

  // "text-area" is not a real input type, so the browser fell back to a one-line text box
  // and long prose (the autoreply takes 4000 characters) was hidden after the first row.
  // The add-on variant stays single-line: a prefix/suffix next to a textarea makes no sense.
  const isTextArea = type === "text-area";

  // Hantera inmatningsändringar
  const handleChange = (e) => {
    if (disabled) return;

    const newValue = e.target.value;

    if (onChange) {
      onChange(e); // Uppdatera externt om "onChange" skickas som prop
    }
    if (newValue === "") {
      setIsValid(null);
    } else if (validate) {
      setIsValid(validate(newValue));
    }
  };

  // Hantera dynamiska klasser för input-fältet
  const inputClasses =
    isValid === null
      ? "input-default"
      : isValid
      ? "input-valid"
      : "input-invalid";

      const displayValue = isNumber ? parseNumber(value) : value;

  return (
    <div className="w-full">
      <label
        htmlFor={inputId}
        className={`block text-sm font-bold mb-2 ${
          isValid === null
            ? ""
            : isValid
            ? "text-secondary-700"
            : "text-error-700"
        }`}
      >
        {label}
      </label>

      {/* Hantera input-fältet med eller utan addOn */}
      {addOn ? (
        <div className="input-add-on-wrapper w-full">
          <input
            id={inputId}
            type={type}
            value={displayValue}
            onChange={handleChange}
            className={`input-add-on-field ${inputClasses} ${className} w-full`}
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            placeholder={placeholder}
            disabled={disabled}  // Add disabled attribute
          />
          <div className="input-add-on">{addOn}</div>
          {/* Extra element bredvid fältet */}
        </div>
      ) : (
        <div className="flex w-full justify-center">
          <div className="relative w-full justify-center">
            {icon && iconLeft && (
              <div>
                <FontAwesomeIcon
                  icon={icon}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black"
                />
              </div>
            )}
            {isTextArea ? (
              <textarea
                id={inputId}
                rows={4}
                value={displayValue}
                onChange={handleChange}
                className={`w-full ${inputClasses}`}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
              />
            ) : (
              <input
                id={inputId}
                type={type}
                value={displayValue}
                onChange={handleChange}
                className={`w-full ${
                  icon ? (iconLeft ? "pl-10" : "pr-10 icon-right") : ""
                } ${inputClasses}`}
                placeholder={placeholder}
                disabled={disabled}  // Add disabled attribute
                maxLength={maxLength}
              />
            )}
            {icon && !iconLeft && (
              <FontAwesomeIcon
                icon={icon}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black"
              />
            )}
          </div>
        </div>
      )}

      {/* Valideringsmeddelande */}
      {validate && isValid !== null && (
        <p
          className={`text-sm ${
            isValid ? "text-secondary-600" : "text-error-600"
          }`}
        >
          {isValid ? "Success" : "Error"}
        </p>
      )}
    </div>
  );
};

export default InputField;

const parseNumber = (value) => {
  if (typeof value !== "string") value = String(value);

  // Replace any commas with periods
  value = value.replace(",", ".");

  // Remove any character that is not a digit, decimal point, or minus sign (for negative numbers)
  value = value.replace(/[^0-9.-]/g, "");

  // Ensure there is only one decimal point
  const parts = value.split(".");
  if (parts.length > 2) {
    // If there are multiple decimal points, keep only the first one
    value = parts[0] + "." + parts.slice(1).join("");
  }

  // Ensure there's only one minus sign and it's at the beginning
  if (value.includes("-")) {
    value = "-" + value.replace(/-/g, ""); // Keep only the first minus sign
  }

  return value;
};
